import type { FieldJob } from "@riea-water/contracts";
import { shortDate } from "./findings-helpers.js";

/** Job types in the prototype's words. */
export const JOB_TYPE_LABEL: Record<FieldJob["type"], string> = {
  read: "Read",
  locate: "Locate",
  verify_serial: "Verify serial",
  meter_test: "Meter test",
  exchange: "Exchange",
  survey_premises: "Survey premises",
};

export const JOB_TYPES = Object.keys(JOB_TYPE_LABEL) as FieldJob["type"][];

export const ABORT_LABEL: Record<NonNullable<FieldJob["abortReason"]>, string> = {
  no_access: "no access",
  unsafe: "unsafe",
  not_found: "not found",
  other: "other",
};

export type Tone = "ok" | "warn" | "crit" | "info" | "off";

/**
 * The status pill: the prototype's words and tones, and one it does not have,
 * a completed exchange whose exchange the registry has not recorded (D-61).
 */
export function jobStatus(job: FieldJob): { label: string; tone: Tone } {
  switch (job.status) {
    case "completed":
      return job.type === "exchange" && !job.exchangeRecorded
        ? { label: "Completed · exchange not recorded", tone: "warn" }
        : { label: "Completed", tone: "ok" };
    case "aborted":
      return {
        label: job.abortReason ? `Aborted · ${ABORT_LABEL[job.abortReason]}` : "Aborted",
        tone: "crit",
      };
    case "dispatched":
      return { label: "Dispatched", tone: "info" };
    case "cancelled":
      return { label: "Cancelled", tone: "off" };
    default:
      return { label: "Raised", tone: "warn" };
  }
}

/** The prototype's "SPID or address" column: a gap site has an address and no SPID. */
export function jobPlace(job: FieldJob): string {
  return job.spid ?? job.target?.address ?? "";
}

export function dueText(job: FieldJob): string {
  return job.dueAt ? shortDate(job.dueAt) : "";
}

export function isOpenJob(job: FieldJob): boolean {
  return job.status === "raised" || job.status === "dispatched";
}

export function needsExchangeRecorded(job: FieldJob): boolean {
  return job.type === "exchange" && job.status === "completed" && !job.exchangeRecorded;
}
